import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../App';

const AD_CUTS = [
  { id: '30s', label: '30 seconds', note: 'Full story — problem, title engine, AI SDR, close. Best for YouTube pre-roll, your site hero and sales meetings.' },
  { id: '15s', label: '15 seconds', note: 'Paid-social workhorse — hook, title proof, close. Fits Meta, TikTok and YouTube 15s slots without a trim.' },
  { id: '6s', label: '6 seconds', note: 'Bumper cut — one proof point and the end card. Best for YouTube bumpers and retargeting.' },
];

const List = ({ title, icon, items, render, testid }) => (
  <div className="p-5 rounded-2xl border border-border bg-surface" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-3">
      <i className={`fa-solid ${icon} mr-2 text-primary`} />{title}
      <span className="ml-2 text-text-primary font-black">{(items || []).length}</span>
    </p>
    {(items || []).length === 0 ? <p className="text-sm text-text-muted">Nothing found on the site for this.</p> : (
      <ul className="space-y-2">{items.map((it, i) => <li key={i} className="text-sm text-text-primary">{render ? render(it) : String(it)}</li>)}</ul>
    )}
  </div>
);

export default function CompanyProfile() {
  const [url, setUrl] = useState('https://samaritansolar.com');
  const [profile, setProfile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cut, setCut] = useState('30s');

  const load = () => {
    api.get('/api/company/profile')
      .then(r => { setProfile(r.data.profile); if (r.data.profile?.source_url) setUrl(r.data.profile.source_url); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runImport = async () => {
    if (!url.trim()) { toast.error('Paste the company website'); return; }
    setImporting(true);
    try {
      const res = await api.post('/api/company/import', { url });
      setProfile(res.data.profile);
      toast.success(`Imported ${res.data.pages} pages from the site`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="company-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Account</p>
          <h1 className="text-3xl font-black tracking-tight text-text-primary">Company Profile</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Pull every service, financing option, warranty, certification, service area and contact detail
            straight off your public website. The AI SDR and Close Copilot only sell what's in here — so nothing
            gets promised that you don't actually offer.
          </p>
        </div>
      </div>

      <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="company-import-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Company website</label>
            <input value={url} onChange={e => setUrl(e.target.value)} className="input-field w-full" placeholder="https://yourcompany.com" data-testid="company-url-input" />
          </div>
          <button onClick={runImport} disabled={importing}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold shadow hover:shadow-lg disabled:opacity-60"
            data-testid="company-import-btn">
            {importing ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Reading the site…</> : <><i className="fa-solid fa-cloud-arrow-down mr-2" />Import everything</>}
          </button>
          {profile?.imported_at && (
            <p className="text-[11px] text-text-muted">
              Last import {String(profile.imported_at).slice(0, 16).replace('T', ' ')} UTC · {(profile.pages_scraped || []).length} pages
            </p>
          )}
        </div>
      </div>

      <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="company-ad-kit">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">
              <i className="fa-solid fa-film mr-2 text-primary" />Ad Kit
            </p>
            <h2 className="text-lg font-black text-text-primary">"Clear to Close" campaign — 30s · 15s · 6s</h2>
            <p className="text-sm text-text-muted mt-1 max-w-2xl">
              Cinematic spot built from the title-engine story: Onyx voiceover over an original ducked music bed,
              burned-in captions for silent autoplay, and a Samaritan logo end card with your phone and website.
              Vertical 9:16 for Reels / TikTok / Shorts, wide 16:9 for YouTube pre-roll and your site hero.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {AD_CUTS.map(c => (
              <button key={c.id} onClick={() => setCut(c.id)} data-testid={`ad-cut-${c.id}`}
                className={`px-4 py-2.5 rounded-xl text-sm font-bold border transition-colors ${cut === c.id
                  ? 'bg-text-primary text-surface border-text-primary'
                  : 'bg-surface-alt border-border text-text-primary hover:border-primary'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-text-muted mb-3" data-testid="ad-cut-note">
          <i className="fa-solid fa-circle-info mr-2 text-primary" />{AD_CUTS.find(c => c.id === cut).note}
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <video key={`v-${cut}`} controls preload="metadata" playsInline poster={`/ad/poster_${cut}_vertical.jpg`}
            className="w-full rounded-xl border border-border max-h-[420px] bg-black"
            src={`/ad/samaritan_${cut}_vertical.mp4`} data-testid="company-ad-vertical" />
          <video key={`w-${cut}`} controls preload="metadata" playsInline poster={`/ad/poster_${cut}_wide.jpg`}
            className="w-full rounded-xl border border-border bg-black self-start"
            src={`/ad/samaritan_${cut}_wide.mp4`} data-testid="company-ad-wide" />
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <a href={`/ad/samaritan_${cut}_vertical.mp4`} download
            className="px-4 py-2.5 rounded-xl bg-text-primary text-surface text-sm font-bold hover:opacity-90" data-testid="company-ad-dl-vertical">
            <i className="fa-solid fa-download mr-2" />Vertical 9:16
          </a>
          <a href={`/ad/samaritan_${cut}_wide.mp4`} download
            className="px-4 py-2.5 rounded-xl bg-surface-alt border border-border text-sm font-bold text-text-primary hover:border-primary" data-testid="company-ad-dl-wide">
            <i className="fa-solid fa-download mr-2" />Wide 16:9
          </a>
          <a href={`/ad/samaritan_${cut}_captions.srt`} download
            className="px-4 py-2.5 rounded-xl bg-surface-alt border border-border text-sm font-bold text-text-primary hover:border-primary" data-testid="company-ad-dl-srt">
            <i className="fa-solid fa-closed-captioning mr-2" />Captions .srt
          </a>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading profile…</div>
      ) : !profile ? (
        <div className="p-12 text-center rounded-2xl border border-border bg-surface" data-testid="company-empty">
          <i className="fa-solid fa-globe text-3xl text-text-muted mb-3" />
          <p className="text-sm text-text-muted">Nothing imported yet. Paste the website above and hit <b>Import everything</b>.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="company-profile">
          <div className="p-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
            <h2 className="text-2xl font-black text-text-primary" data-testid="company-name">{profile.company_name || 'Company'}</h2>
            {profile.tagline && <p className="text-sm font-bold text-primary mt-1">{profile.tagline}</p>}
            {profile.about && <p className="text-sm text-text-muted mt-3 max-w-4xl">{profile.about}</p>}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="company-contacts">
              {(profile.phones || []).map(p => (
                <a key={p} href={`tel:${p.replace(/\D/g, '')}`} className="px-3 py-1.5 rounded-xl bg-surface border border-border text-[12px] font-bold text-text-primary hover:border-primary">
                  <i className="fa-solid fa-phone mr-1.5 text-primary" />{p}
                </a>
              ))}
              {(profile.emails || []).map(e => (
                <a key={e} href={`mailto:${e}`} className="px-3 py-1.5 rounded-xl bg-surface border border-border text-[12px] font-bold text-text-primary hover:border-primary">
                  <i className="fa-solid fa-envelope mr-1.5 text-primary" />{e}
                </a>
              ))}
              {(profile.addresses || []).map(a => (
                <span key={a} className="px-3 py-1.5 rounded-xl bg-surface border border-border text-[12px] font-bold text-text-primary">
                  <i className="fa-solid fa-location-dot mr-1.5 text-primary" />{a}
                </span>
              ))}
              {profile.years_experience && (
                <span className="px-3 py-1.5 rounded-xl bg-surface border border-border text-[12px] font-bold text-text-primary">
                  <i className="fa-solid fa-award mr-1.5 text-primary" />{profile.years_experience}
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="company-services">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-3">
                <i className="fa-solid fa-screwdriver-wrench mr-2 text-primary" />Services we actually sell
                <span className="ml-2 text-text-primary font-black">{(profile.services || []).length}</span>
              </p>
              <div className="space-y-2">
                {(profile.services || []).map((s, i) => (
                  <div key={i} className="p-3 rounded-xl bg-surface-alt border border-border">
                    <p className="text-sm font-black text-text-primary">{s.name}</p>
                    {s.summary && <p className="text-[12px] text-text-muted mt-0.5">{s.summary}</p>}
                  </div>
                ))}
                {(profile.services || []).length === 0 && <p className="text-sm text-text-muted">No services detected.</p>}
              </div>
            </div>

            <List title="Financing on offer" icon="fa-hand-holding-dollar" items={profile.financing} testid="company-financing" />
            <List title="Warranties" icon="fa-shield-halved" items={profile.warranties} testid="company-warranties" />
            <List title="Certifications" icon="fa-certificate" items={profile.certifications} testid="company-certifications" />
            <List title="Service areas" icon="fa-map" items={profile.service_areas} testid="company-areas" />
            <List title="Equipment brands" icon="fa-solar-panel" items={profile.brands_installed} testid="company-brands" />
            <List title="Why homeowners pick us" icon="fa-trophy" items={profile.differentiators} testid="company-diff" />
            <List title="Team" icon="fa-users" items={profile.team} testid="company-team"
              render={(t) => <span><b>{t.name}</b>{t.role ? ` — ${t.role}` : ''}</span>} />
            <List title="Reviews pulled from the site" icon="fa-quote-left" items={profile.reviews} testid="company-reviews"
              render={(r) => <span>“{r.quote}” <span className="text-text-muted">— {r.author}</span></span>} />
          </div>

          <p className="text-[11px] text-text-muted" data-testid="company-source">
            Source: <a href={profile.source_url} target="_blank" rel="noreferrer" className="underline">{profile.source_url}</a>
            {(profile.pages_scraped || []).length ? ` · ${profile.pages_scraped.length} pages read` : ''}
            {' '}· this fact sheet is injected into every AI SDR touch and every Close Copilot battle card.
          </p>
        </div>
      )}
    </div>
  );
}
